import { Box } from '@mui/material';
import { useRef } from 'react';
import { TitlePageLayout } from '@/admins/SmartReports/components/Layouts/TitlePage';
import { PageNumber } from '@/admins/SmartReports/components/StaticContent/PageNumber';
import { WatermarkLogo } from '@/admins/SmartReports/components/StaticContent/WatermarkLogo';
import { cardVariants } from '@/pages/ValuationsV2/theme';
import { combineSx } from '@/utils/combineSx';
import { SmartReportLayouts } from '../Layouts';
import type { EditSmartReportContextType } from './EditSmartReportContext';
import { useEditSmartReport } from './EditSmartReportContext';
import { useEditPageScale } from './hooks';

const editPageStyles = {
  position: 'relative',
  display: 'block',
  width: 'clamp(var(--w), 100%, 100%)',
  aspectRatio: 'var(--aspectRatio)',
  transformOrigin: 'top left',
};

const editPageWrapperStyles = {
  '--w': '1280px',
  '--aspectRatio': '16/9',
  position: 'relative',
  margin: 'auto',
  overflow: 'hidden',
  width: 'min(100cqw, calc(100cqh * 16 / 9))',
  aspectRatio: 'var(--aspectRatio)',
};

export const EditPage = () => {
  const editPageWrapperRef = useRef<HTMLDivElement>(null);
  const editPageRef = useRef<HTMLDivElement>(null);
  const context = useEditSmartReport() as EditSmartReportContextType | null;

  const scale = useEditPageScale({
    wrapper: editPageWrapperRef,
    content: editPageRef,
  });

  if (!context) {
    return null;
  }

  const { selectedPage, entity, onComponentLoad } = context;
  const Layout =
    SmartReportLayouts.find(l => l.id === selectedPage?.layout) || SmartReportLayouts[0];

  const showFooter = Layout?.id !== TitlePageLayout.id;

  if (!Layout) {
    return null;
  }

  return (
    <Box sx={{ height: '100%', containerType: 'size' }}>
      <Box
        sx={combineSx(
          editPageWrapperStyles,
          cardVariants({ interactive: false, padding: 'none', border: true }),
        )}
        ref={editPageWrapperRef}
        data-id='edit-page-wrapper'
      >
        <Box
          sx={{
            ...editPageStyles,
            scale: `${scale} ${scale} ${scale}`,
          }}
          data-id='edit-page'
          ref={editPageRef}
        >
          <Layout.Component
            key={selectedPage?.number}
            /** @ts-expect-error */
            entity={entity}
            onComponentLoad={onComponentLoad}
          />

          {showFooter && (
            <>
              <WatermarkLogo />
              <PageNumber pageNumber={String(selectedPage?.number)} />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};
